import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  SxProps,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import ColorConstant from '../../constants/ColorConstant';
import { ProviderEnum } from '../../constants/ProviderEnum';
import { IOrder, OrderStatus } from '../../interfaces/IOrder';
import IUser from '../../interfaces/IUser';
import AccountsService from '../../services/AccountsService';
import OrderService from '../../services/OrderService';
import SnackbarService from '../../services/SnackbarService';
import OrderDetailsModalList from '../OrderDetailsModalList/OrderDetailsModalList';

const styles: { [key: string]: SxProps } = {
  modalWrapper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '30%',
    minWidth: '500px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeaderWrapper: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: `1px solid ${ ColorConstant.lightGray }`,
  },
  modalHeader: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 32px',
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    p: 2,
  },
  diplayInCenter: {
    display: 'flex',
    justifyContent: 'center',
    fontSize: '14px',
    p: 2,
  },
  errorMsg: {
    padding: '8px 32px',
    fontSize: '.8rem',
    color: ColorConstant.theme.palette.warning.main,
  },
};

export default function OrderDetailsModal({ isOrderDetailsModalOpen, order, investmentAccounts, handleCloseModal }: IOrderDetailsModalProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [resumeBtnIsLoading, setResumeBtnIsLoading] = useState(false);
  const [deleteBtnIsLoading, setDeleteBtnIsLoading] = useState(false);

  const abortControllerRef = useRef(new AbortController());
  const orderService = new OrderService(abortControllerRef.current.signal);
  const accountService = new AccountsService(abortControllerRef.current.signal);

  useEffect(() => {
    return () => {
      abortControllerRef.current.abort();
    };
  }, []);

  const handleIsMenuOpen = (opening: boolean, event?: React.MouseEvent<HTMLElement>) => {
    setIsMenuOpen(opening);
    setAnchorEl(opening ? event?.currentTarget ?? null : null);
  };

  const resumeRequest = async () => {
    setResumeBtnIsLoading(true);
    try {
      await orderService.resumeRequest(order['@id']);
      SnackbarService.open(t('success_resume_request'), 'info');
    } catch (error) {
      SnackbarService.open(t('failed_resume_request'), 'error');
      console.error(error);
    } finally {
      setResumeBtnIsLoading(false);
      setIsMenuOpen(false);
    }
  };

  const deleteInvestmentOperationWorkflow = async () => {
    setDeleteBtnIsLoading(true);
    try {
      await accountService.deleteInvestmentOperationWorkflow(order['@id']);
      SnackbarService.open(t('success_delete_request'), 'success');
      await handleCloseModal(true);
    } catch (error) {
      const errorMsg = `${ t('failed_delete_request') } : ${
        (error as AxiosError<{ 'hydra:description': string }>).response?.data['hydra:description']
      }`;

      SnackbarService.open(errorMsg, 'error');
      console.error(error);
    } finally {
      setDeleteBtnIsLoading(false);
      setIsMenuOpen(false);
    }
  };

  const investmentAccount = investmentAccounts?.[0];
  const provider = investmentAccount?.provider;

  const isResumeItemDisabled =
    !order.error && provider !== ProviderEnum.GENERALI && order.status !== OrderStatus.STATUS_PENDING;

  const isDeleteItemDisabled =
    ![
      OrderStatus.STATUS_CREATION,
      OrderStatus.STATUS_TO_SIGN,
      OrderStatus.STATUS_FAILED,
    ].includes(order.status as OrderStatus) || order['@type'] !== 'InvestmentOperationWorkflow';

  return (
    <>
      <Modal open={isOrderDetailsModalOpen} onClose={() => handleCloseModal()}>
        <Card sx={styles.modalWrapper}>
          <Box sx={styles.modalHeaderWrapper}>
            <Box sx={styles.modalHeader}>
              <Typography variant="h5">
                <Trans>{ `order_type_${ order.type }` }</Trans>&nbsp;
                { order.type === 'buy' && <Trans>{ `order_monthly_${ order.monthlyInvestment }` }</Trans> }
              </Typography>
              <Typography variant="h5">{ order.amountOrdered ?? '-' }€</Typography>
            </Box>

            <IconButton onClick={(evt) => handleIsMenuOpen(true, evt)}>
              <MoreVertIcon fontSize="large" />
            </IconButton>
          </Box>

          <CardContent sx={{ p: 0 }}>
            { order.error && (
              <Typography sx={styles.errorMsg}>
                <Trans>error</Trans> : { order.error }
              </Typography>
            ) }
            <OrderDetailsModalList order={order} />
          </CardContent>

          <Box sx={styles.buttonWrapper}>
            <Button color="secondary" variant="contained" onClick={() => handleCloseModal()}>
              <Trans>close_button</Trans>
            </Button>
          </Box>
        </Card>
      </Modal>

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={() => handleIsMenuOpen(false)}>
        <MenuItem onClick={resumeRequest} disabled={isResumeItemDisabled || resumeBtnIsLoading} sx={styles.diplayInCenter}>
          { resumeBtnIsLoading ? <CircularProgress size={24} /> : <Trans>resume</Trans> }
        </MenuItem>
        <MenuItem onClick={deleteInvestmentOperationWorkflow} disabled={isDeleteItemDisabled || deleteBtnIsLoading} sx={styles.diplayInCenter}>
          { deleteBtnIsLoading ? <CircularProgress size={24} /> : <Trans>delete</Trans> }
        </MenuItem>
      </Menu>
    </>
  );
}


interface IOrderDetailsModalProps {
  isOrderDetailsModalOpen: boolean;
  order: IOrder;
  investmentAccounts: IUser['investmentAccounts'];
  handleCloseModal(hasChangedValue?: boolean): Promise<void>;

}
